import React, { useEffect, useState, useCallback } from "react";
import { Typography, Button, Input, Select } from "antd";
import {
  DashboardOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  FormOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
  FilterOutlined,
  DownOutlined,
} from "@ant-design/icons";
import {
  Calendar03Icon,
  Task01Icon,
  Video01Icon,
  Call02Icon,
} from "hugeicons-react";
import { getInvoices } from "../../../services/invoiceService";
import { getQuotations } from "../../../services/quotationService";
import { getProposals, getProposalSummary } from "../../../services/proposalService";
import { getPipelineBoard } from "../../../services/pipelineService";
import { getEmployees } from "../../../services/employeeService";
import useAuthSession from "../../../hooks/useAuthSession";
import "./quickOverview.css";

const { Text } = Typography;

function getActiveTabType(activeTab) {
  if (activeTab === "invoice" || activeTab === "invoices") return "invoices";
  if (activeTab === "quotation" || activeTab === "quotations") return "quotations";
  if (activeTab === "proposal" || activeTab === "proposals") return "proposals";
  if (activeTab === "pipeline") return "pipeline";
  if (activeTab === "activities") return "activities";
  return "other";
}

async function fetchInvoiceMetrics(companyCode, employeeId) {
  try {
    const options = {};
    if (employeeId) options.employeeId = employeeId;
    const res = await getInvoices(companyCode, options);
    if (!res?.success) return null;

    const invList = Array.isArray(res.invoices || res.items) ? res.invoices || res.items : [];
    const s = res.stats || {};
    const totalAmount = Number(s.totalAmount) || invList.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
    const totalPaid = Number(s.totalPaid) || invList.reduce((sum, i) => sum + (Number(i.amountPaid) || (i.paymentStatus === "paid" ? Number(i.total || 0) : 0)), 0);
    const totalPending = Number(s.totalPending) || invList.reduce((sum, i) => sum + (i.balanceDue !== undefined && !Number.isNaN(Number(i.balanceDue)) ? Number(i.balanceDue) : Math.max(0, (Number(i.total) || 0) - (Number(i.amountPaid) || 0))), 0);
    const clientSet = new Set(invList.map((i) => i.clientId || i.clientSnapshot?.clientId || i.leadCompanyName).filter(Boolean));

    return {
      stats: { totalAmount, totalPaid, totalPending },
      clientCount: clientSet.size,
    };
  } catch (e) {
    console.warn("Failed to load invoice stats for QuickOverview", e);
    return null;
  }
}

async function fetchQuotationMetrics(companyCode, employeeId) {
  try {
    const options = {};
    if (employeeId) options.employeeId = employeeId;
    const res = await getQuotations(companyCode, options);
    if (!res?.success) return null;

    const totalAmt = Number(res.stats?.totalAmount || 0);
    const qCount = Array.isArray(res.quotations) ? res.quotations.length : res.stats?.quotationCount || 0;
    const avgVal = qCount > 0 ? Math.round(totalAmt / qCount) : 0;

    let lCount = 0;
    if (Array.isArray(res.quotations)) {
      const leadSet = new Set(res.quotations.map((q) => q.clientId || q.leadId || q.leadCompanyName).filter(Boolean));
      lCount = leadSet.size;
    }

    return {
      totalAmount: totalAmt,
      quotationCount: qCount,
      avgValue: avgVal,
      leadCount: lCount,
    };
  } catch (e) {
    console.warn("Failed to load quotation stats for QuickOverview", e);
    return null;
  }
}

async function fetchProposalMetrics(companyCode, employeeId) {
  try {
    const options = {};
    if (employeeId) options.employeeId = employeeId;
    const res = await getProposalSummary(companyCode, options);
    if (res?.success) {
      return {
        totalCount: res.totalProposals || 0,
        accountCount: res.engagedAccounts || 0,
      };
    }
    // Fallback if summary endpoint returned error
    const fallback = await getProposals(companyCode, options);
    if (!fallback?.success || !Array.isArray(fallback.proposals)) return null;
    const leadSet = new Set(fallback.proposals.map((p) => p.clientId || p.leadId || p.leadCompanyName || p.companyName).filter(Boolean));
    return {
      totalCount: fallback.total || fallback.proposals.length,
      accountCount: leadSet.size,
    };
  } catch (e) {
    console.warn("Failed to load proposal stats for QuickOverview", e);
    return null;
  }
}

function calculatePipelineStats(columns) {
  const allDeals = [];
  Object.entries(columns || {}).forEach(([stg, col]) => {
    if (col && Array.isArray(col.leads)) {
      col.leads.forEach((ld) => {
        allDeals.push({
          amount: Number(ld.amount || ld.quoteAmount || ld.totalAmount || 0),
          pipelineStage: ld.pipelineStage || stg,
        });
      });
    }
  });

  const openDeals = allDeals.filter((d) => d.pipelineStage !== "CLOSED_WON" && d.pipelineStage !== "CLOSED_LOST");
  const wonDeals = allDeals.filter((d) => d.pipelineStage === "CLOSED_WON");
  const lostDeals = allDeals.filter((d) => d.pipelineStage === "CLOSED_LOST");

  return {
    openCount: openDeals.length,
    openValue: openDeals.reduce((sum, d) => sum + d.amount, 0),
    wonCount: wonDeals.length,
    wonValue: wonDeals.reduce((sum, d) => sum + d.amount, 0),
    lostCount: lostDeals.length,
    lostValue: lostDeals.reduce((sum, d) => sum + d.amount, 0),
  };
}

async function fetchPipelineMetrics(companyCode, isEmployee, employeeId) {
  try {
    const filters = {};
    if (isEmployee && employeeId) filters.employeeId = employeeId;
    const res = await getPipelineBoard(companyCode, filters, 100);
    if (!res?.success || !res.columns) return null;
    return calculatePipelineStats(res.columns);
  } catch (e) {
    console.warn("Failed to load pipeline stats for QuickOverview", e);
    return null;
  }
}

function ActivitySubTabs({ activitySubTab, onActivitySubTabChange }) {
  const tabs = [
    { key: "calendar", label: "Calendar", icon: <Calendar03Icon size={16} strokeWidth={2} /> },
    { key: "tasks", label: "Tasks", icon: <Task01Icon size={16} strokeWidth={2} /> },
    { key: "meetings", label: "Meetings", icon: <Video01Icon size={16} strokeWidth={2} /> },
    { key: "calls", label: "Calls", icon: <Call02Icon size={16} strokeWidth={2} /> },
  ];

  return (
    <div className="qo-activity-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`qo-tab-btn ${activitySubTab === tab.key ? "active" : ""}`}
          onClick={() => onActivitySubTabChange?.(tab.key)}
        >
          <span className="qo-tab-icon">{tab.icon}</span>
          <span className="qo-tab-label">{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

function OverviewHeaderTitle({ icon, title }) {
  return (
    <>
      {icon}
      <Text strong style={{ fontSize: "0.85rem", color: "#0f172a" }}>
        {title}
      </Text>
    </>
  );
}

function PipelineHeaderWithFilters({ isEmployee, pipelineFilters = { search: "", owner: "all", priority: "all" }, onPipelineFiltersChange, onAddDeal, employees = [] }) {
  const search = pipelineFilters.search || "";
  const owner = pipelineFilters.owner || "all";
  const priority = pipelineFilters.priority || "all";

  const handleSearchChange = (val) => {
    onPipelineFiltersChange?.({ ...pipelineFilters, search: val });
  };
  const handleOwnerChange = (val) => {
    onPipelineFiltersChange?.({ ...pipelineFilters, owner: val });
  };
  const handlePriorityChange = (val) => {
    onPipelineFiltersChange?.({ ...pipelineFilters, priority: val });
  };

  const repOptions = [
    {
      value: "all",
      label: (
        <span className="qo-select-label">
          <UserOutlined className="qo-filter-icon" /> All Reps
        </span>
      ),
    },
    ...employees.map((emp) => ({
      value: emp._id,
      label: (
        <span className="qo-select-label">
          <UserOutlined className="qo-filter-icon" /> {emp.name}
        </span>
      ),
    })),
  ];

  const priorityOptions = [
    {
      value: "all",
      label: (
        <span className="qo-select-label">
          <FilterOutlined className="qo-filter-icon" /> All Priorities
        </span>
      ),
    },
    {
      value: "High",
      label: (
        <span className="qo-select-label">
          <span className="qo-prio-dot prio-high" /> High Priority
        </span>
      ),
    },
    {
      value: "Medium",
      label: (
        <span className="qo-select-label">
          <span className="qo-prio-dot prio-medium" /> Medium Priority
        </span>
      ),
    },
    {
      value: "Low",
      label: (
        <span className="qo-select-label">
          <span className="qo-prio-dot prio-low" /> Low Priority
        </span>
      ),
    },
  ];

  return (
    <div className="qo-pipeline-left-controls">
      <Input
        className="qo-search-input"
        prefix={<SearchOutlined className="qo-control-icon" />}
        placeholder="Search company or deal..."
        value={search}
        onChange={(e) => handleSearchChange(e.target.value)}
        allowClear
      />

      {!isEmployee && (
        <Select
          className={`qo-filter-select ${owner !== "all" ? "active-filter" : ""}`}
          value={owner}
          onChange={handleOwnerChange}
          suffixIcon={<DownOutlined />}
          style={{ minWidth: 135 }}
          options={repOptions}
        />
      )}

      <Select
        className={`qo-filter-select ${priority !== "all" ? "active-filter" : ""}`}
        value={priority}
        onChange={handlePriorityChange}
        suffixIcon={<DownOutlined />}
        style={{ minWidth: 140 }}
        options={priorityOptions}
      />
    </div>
  );
}

function OverviewTabHeader({ tabType, activitySubTab, onActivitySubTabChange, isEmployee, pipelineFilters, onPipelineFiltersChange, onAddDeal, employees }) {
  if (tabType === "activities") {
    return <ActivitySubTabs activitySubTab={activitySubTab} onActivitySubTabChange={onActivitySubTabChange} />;
  }

  if (tabType === "pipeline") {
    return <PipelineHeaderWithFilters isEmployee={isEmployee} pipelineFilters={pipelineFilters} onPipelineFiltersChange={onPipelineFiltersChange} onAddDeal={onAddDeal} employees={employees} />;
  }

  const headerConfig = {
    invoices: {
      icon: <FileTextOutlined style={{ color: "#2563eb", fontSize: "0.95rem" }} />,
      title: "Invoices Overview",
    },
    quotations: {
      icon: <CalculatorOutlined style={{ color: "#2563eb", fontSize: "0.95rem" }} />,
      title: "Quotations Overview",
    },
    proposals: {
      icon: <FormOutlined style={{ color: "#2563eb", fontSize: "0.95rem" }} />,
      title: "Proposals Overview",
    },
  };

  const current = headerConfig[tabType] || {
    icon: <DashboardOutlined style={{ color: "#2563eb", fontSize: "0.95rem" }} />,
    title: "Quick Overview",
  };

  return <OverviewHeaderTitle icon={current.icon} title={current.title} />;
}

const renderRupee = (value) => (
  <>
    <span className="qo-currency">₹</span>
    {Math.round(Number(value || 0)).toLocaleString("en-IN")}
  </>
);

function InvoiceMetricsGroup({ invoiceStats, clientCount }) {
  return (
    <div className="qo-metric-group">
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Total Invoiced:</span>
        <span className="qo-metric-value">{renderRupee(invoiceStats.totalAmount)}</span>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Total Collected:</span>
        <span className="qo-metric-value">{renderRupee(invoiceStats.totalPaid)}</span>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Pending Balance:</span>
        <span className="qo-metric-value">{renderRupee(invoiceStats.totalPending)}</span>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Active Clients:</span>
        <span className="qo-metric-value">{clientCount}</span>
      </div>
    </div>
  );
}

function QuotationMetricsGroup({ quotationStats }) {
  return (
    <div className="qo-metric-group">
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Total Pipeline:</span>
        <span className="qo-metric-value">{renderRupee(quotationStats.totalAmount)}</span>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Avg Quote Value:</span>
        <span className="qo-metric-value">{renderRupee(quotationStats.avgValue)}</span>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Prospective Leads:</span>
        <span className="qo-metric-value">{quotationStats.leadCount}</span>
      </div>
    </div>
  );
}

function ProposalMetricsGroup({ proposalStats }) {
  return (
    <div className="qo-metric-group">
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Total Proposals:</span>
        <span className="qo-metric-value">{proposalStats.totalCount}</span>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Engaged Accounts:</span>
        <span className="qo-metric-value">{proposalStats.accountCount}</span>
      </div>
    </div>
  );
}

function PipelineMetricsGroup({ pipelineStats }) {
  return (
    <div className="qo-metric-group">
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Open Pipeline:</span>
        <div className="qo-metric-value-wrap">
          <span className="qo-metric-value">{renderRupee(pipelineStats.openValue)}</span>
          <span className="qo-metric-subtext">({pipelineStats.openCount} deals)</span>
        </div>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Closed Won:</span>
        <div className="qo-metric-value-wrap">
          <span className="qo-metric-value">{renderRupee(pipelineStats.wonValue)}</span>
          <span className="qo-metric-subtext">({pipelineStats.wonCount} won)</span>
        </div>
      </div>
      <div className="qo-divider" />
      <div className="qo-metric-pill">
        <span className="qo-metric-label">Closed Lost:</span>
        <div className="qo-metric-value-wrap">
          <span className="qo-metric-value text-loss">{renderRupee(pipelineStats.lostValue)}</span>
          <span className="qo-metric-subtext">({pipelineStats.lostCount} lost)</span>
        </div>
      </div>
    </div>
  );
}

function OverviewMetricsRight({ tabType, onCreateActivity, invoiceStats, clientCount, quotationStats, proposalStats, pipelineStats }) {
  if (tabType === "activities") {
    return (
      <Button type="primary" icon={<PlusOutlined />} onClick={onCreateActivity} style={{ borderRadius: 8, height: 34, padding: "0 16px" }}>
        Create Activity
      </Button>
    );
  }
  if (tabType === "invoices") {
    return <InvoiceMetricsGroup invoiceStats={invoiceStats} clientCount={clientCount} />;
  }
  if (tabType === "quotations") {
    return <QuotationMetricsGroup quotationStats={quotationStats} />;
  }
  if (tabType === "proposals") {
    return <ProposalMetricsGroup proposalStats={proposalStats} />;
  }
  if (tabType === "pipeline") {
    return <PipelineMetricsGroup pipelineStats={pipelineStats} />;
  }
  return null;
}

/**
 * QuickOverview Component
 * Sticky bar positioned directly below TopNavbar (top: 64px) with smooth appear/disappear animation.
 * Automatically displays contextual metrics/controls for Invoices, Quotations, Proposals, Pipeline, and Activities.
 */
function QuickOverview({ visible = true, role: _propRole, employeeId: propEmployeeId, activeTab = "overview", onNavigateTab: _onNavigateTab, activitySubTab = "calendar", onActivitySubTabChange, onCreateActivity, pipelineFilters, onPipelineFiltersChange, onAddDeal }) {
  const { companyCode, isEmployee, employeeId: sessionEmployeeId } = useAuthSession();
  const effectiveEmployeeId = propEmployeeId || sessionEmployeeId;
  const tabType = getActiveTabType(activeTab);
  const isOverviewVisible = visible && tabType !== "other";

  // Invoices metrics
  const [invoiceStats, setInvoiceStats] = useState({ totalAmount: 0, totalPaid: 0, totalPending: 0 });
  const [clientCount, setClientCount] = useState(0);

  // Quotations metrics
  const [quotationStats, setQuotationStats] = useState({ totalAmount: 0, quotationCount: 0, avgValue: 0, leadCount: 0 });

  // Proposals metrics
  const [proposalStats, setProposalStats] = useState({ totalCount: 0, accountCount: 0 });

  // Pipeline metrics
  const [pipelineStats, setPipelineStats] = useState({
    openCount: 0,
    openValue: 0,
    wonCount: 0,
    wonValue: 0,
    lostCount: 0,
    lostValue: 0,
  });

  // Employees for pipeline filters dropdown
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (tabType === "pipeline" && !isEmployee && companyCode) {
      getEmployees(companyCode)
        .then((res) => {
          if (res?.success && Array.isArray(res.employees)) {
            setEmployees(res.employees);
          }
        })
        .catch((err) => console.warn("Failed to load employees for QuickOverview:", err));
    }
  }, [tabType, isEmployee, companyCode]);

  const fetchData = useCallback(async () => {
    if (!companyCode) return;

    if (tabType === "invoices") {
      const data = await fetchInvoiceMetrics(companyCode, effectiveEmployeeId);
      if (data) {
        setInvoiceStats(data.stats);
        setClientCount(data.clientCount);
      }
    } else if (tabType === "quotations") {
      const data = await fetchQuotationMetrics(companyCode, effectiveEmployeeId);
      if (data) setQuotationStats(data);
    } else if (tabType === "proposals") {
      const data = await fetchProposalMetrics(companyCode, effectiveEmployeeId);
      if (data) setProposalStats(data);
    } else if (tabType === "pipeline") {
      const data = await fetchPipelineMetrics(companyCode, isEmployee, effectiveEmployeeId);
      if (data) setPipelineStats(data);
    }
  }, [companyCode, tabType, effectiveEmployeeId, isEmployee]);

  useEffect(() => {
    if (tabType !== "other" && tabType !== "activities") {
      fetchData();
    }
  }, [tabType, fetchData]);

  if (!isOverviewVisible) return null;

  return (
    <div className={`quick-overview-bar ${isOverviewVisible ? "" : "quick-overview-hidden"}`}>
      <div className="quick-overview-left">
        <OverviewTabHeader tabType={tabType} activitySubTab={activitySubTab} onActivitySubTabChange={onActivitySubTabChange} isEmployee={isEmployee} pipelineFilters={pipelineFilters} onPipelineFiltersChange={onPipelineFiltersChange} onAddDeal={onAddDeal} employees={employees} />
      </div>

      <div className="quick-overview-right">
        <OverviewMetricsRight tabType={tabType} onCreateActivity={onCreateActivity} invoiceStats={invoiceStats} clientCount={clientCount} quotationStats={quotationStats} proposalStats={proposalStats} pipelineStats={pipelineStats} />
      </div>
    </div>
  );
}

export default QuickOverview;
